/**
 * Image Hashing Utility
 * Generates SHA-256 hash for image files to detect duplicates
 */

import { db } from '../db';
import axios from 'axios';
import config from '../config';

/**
 * Generate SHA-256 hash for a file
 * @param {File} file - The image file to hash
 * @returns {Promise<string>} - The hexadecimal hash string
 */
export const generateImageHash = async (file) => {
  try {
    // Use FileReader for better Safari compatibility
    const buffer = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
    
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  } catch (error) {
    console.error('Error generating image hash:', error);
    throw new Error('Failed to generate image hash');
  }
};

/** Dedupe hashing for the same File reference (e.g. duplicate check + other-eye compare). */
const imageHashPromiseCache = new WeakMap();

/**
 * @param {File} file
 * @returns {Promise<string>}
 */
export const getCachedImageHash = async (file) => {
  let pending = imageHashPromiseCache.get(file);
  if (pending) return pending;
  pending = generateImageHash(file);
  imageHashPromiseCache.set(file, pending);
  return pending;
};

/**
 * Check if image hash exists in local database
 * @param {string} hash - The image hash to check
 * @returns {Promise<Object>} - Duplicate information
 */
export const checkLocalImageHash = async (hash) => {
  try {
    const duplicates = await db.image_hashes
      .where('hash').equals(hash)
      .toArray();
    
    return {
      isDuplicate: duplicates.length > 0,
      duplicates: duplicates
    };
  } catch (error) {
    console.error('Error checking local image hash:', error);
    return { isDuplicate: false, duplicates: [] };
  }
};

/**
 * Check if image hash exists on backend
 * @param {string} hash - The image hash to check
 * @returns {Promise<Object>} - Duplicate information
 */
export const checkBackendImageHash = async (hash) => {
  if (!navigator.onLine) {
    return { isDuplicate: false, duplicates: [] };
  }

  try {
    const header = { "Authorization": "Token " + localStorage.getItem("optha-token") };
    const response = await axios.post(`${config.API_BASE_URL}/api/reports/image-hashes/check-duplicates/`, {
      hashes: [hash]
    }, { headers: header });
    
    return {
      isDuplicate: response.data.duplicates.length > 0,
      duplicates: response.data.duplicates
    };
  } catch (error) {
    console.error('Error checking backend image hash:', error);
    return { isDuplicate: false, duplicates: [] };
  }
};

/**
 * Comprehensive duplicate check (local + backend)
 * @param {File} file - The image file to check
 * @returns {Promise<Object>} - Duplicate information
 */
export const checkImageDuplicates = async (file) => {
  try {
    const hash = await getCachedImageHash(file);
    
    // Check local database first
    const localResult = await checkLocalImageHash(hash);
    if (localResult.isDuplicate) {
      return {
        ...localResult,
        hash: hash,
        source: 'local'
      };
    }
    
    // Check backend if online
    const backendResult = await checkBackendImageHash(hash);
    if (backendResult.isDuplicate) {
      return {
        ...backendResult,
        hash: hash,
        source: 'backend'
      };
    }
    
    return {
      isDuplicate: false,
      duplicates: [],
      hash: hash,
      source: 'none'
    };
  } catch (error) {
    console.error('Error checking image duplicates:', error);
    return { isDuplicate: false, duplicates: [], hash: null, source: 'error' };
  }
};
